/**
 * WEB-3956 юнит 8 — раздел маршрутов «админ-панель» (WEB-471 BC-4: Здоровье
 * и Управление). На `admin.<apex>` `router/index.tsx` монтирует панель в
 * корень домена (`ADMIN_ROOT_ELEMENT` под `AdminRoute`, дети —
 * `ADMIN_PANEL_CHILDREN`); на прочих хостах остаётся только перенаправление
 * старых адресов `/admin/*`.
 */
import { Navigate, type RouteObject } from 'react-router-dom';
import { AdminRoute } from '../AdminRoute';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { SuspenseWrap } from '../suspense';

// WEB-471 (BC-4): admin panel skeleton — Здоровье (health) + Управление (manage).
const AdminLayoutPage = lazyWithRetry(
  () => import('@/pages/admin/AdminLayout'),
  'page:AdminLayout',
);
const AdminPulsePage = lazyWithRetry(
  () => import('@/pages/admin/health/AdminPulsePage'),
  'page:AdminPulsePage',
);
const AdminErrorsPage = lazyWithRetry(
  () => import('@/pages/admin/health/AdminErrorsPage'),
  'page:AdminErrorsPage',
);
const AdminMoneyPage = lazyWithRetry(
  () => import('@/pages/admin/health/AdminMoneyPage'),
  'page:AdminMoneyPage',
);
const AdminAvailabilityPage = lazyWithRetry(
  () => import('@/pages/admin/health/AdminAvailabilityPage'),
  'page:AdminAvailabilityPage',
);
const AdminQueuesPage = lazyWithRetry(
  () => import('@/pages/admin/health/AdminQueuesPage'),
  'page:AdminQueuesPage',
);
const AdminAiQualityPage = lazyWithRetry(
  () => import('@/pages/admin/health/AdminAiQualityPage'),
  'page:AdminAiQualityPage',
);
// WEB-1992: «Скорость воронок» — адрес, на который уже ведёт тревога по LCP
// (admin-health-vitals.service.ts, target '/health/vitals').
const AdminVitalsPage = lazyWithRetry(
  () => import('@/pages/admin/health/AdminVitalsPage'),
  'page:AdminVitalsPage',
);
// WEB-2064 шаг 1: «Скорость ручек» — серверные перцентили времени ответа.
// Тревога «ручка тормозит» ведёт сюда (admin-health-latency.service.ts,
// target '/health/latency').
const AdminLatencyPage = lazyWithRetry(
  () => import('@/pages/admin/health/AdminLatencyPage'),
  'page:AdminLatencyPage',
);
// WEB-2898: «Conveyor» — конвейер пачки, машина оркестратора, команды ему.
const AdminConveyorPage = lazyWithRetry(
  () => import('@/pages/admin/health/AdminConveyorPage'),
  'page:AdminConveyorPage',
);
const AdminClientsPage = lazyWithRetry(
  () => import('@/pages/admin/manage/AdminClientsPage'),
  'page:AdminClientsPage',
);
const AdminProjectsPage = lazyWithRetry(
  () => import('@/pages/admin/manage/AdminProjectsPage'),
  'page:AdminProjectsPage',
);
const AdminAuditPage = lazyWithRetry(
  () => import('@/pages/admin/manage/AdminAuditPage'),
  'page:AdminAuditPage',
);
const AdminBillingPage = lazyWithRetry(
  () => import('@/pages/admin/manage/AdminBillingPage'),
  'page:AdminBillingPage',
);
// WEB-2331: «Переезд на subster.ai» — кто ещё смотрит на старые адреса.
const AdminMigrationPage = lazyWithRetry(
  () => import('@/pages/admin/manage/AdminMigrationPage'),
  'page:AdminMigrationPage',
);
// WEB-3469: «Уведомления» — какие письма получает каждый администратор.
const AdminNotificationsPage = lazyWithRetry(
  () => import('@/pages/admin/manage/AdminNotificationsPage'),
  'page:AdminNotificationsPage',
);

// WEB-471: on admin.<apex> the panel IS the app — mounted at the domain root,
// so its URLs are /health/pulse, /manage/audit … (no redundant /admin prefix,
// since the subdomain already scopes it). Backend API paths stay /admin/*.
// Объявление без аннотации типа и с `];` в начале строки: блок читают текстом
// сторожа `adminPanelNavRoutes.web2064`, `AdminLayout.sectionTitles.web1368` и
// серверный `admin-health-alerts.targets.spec.ts`.
export const ADMIN_PANEL_CHILDREN = [
  { index: true, element: <Navigate to="/health/pulse" replace /> },
  { path: 'health/pulse', element: <SuspenseWrap><AdminPulsePage /></SuspenseWrap> },
  { path: 'health/errors', element: <SuspenseWrap><AdminErrorsPage /></SuspenseWrap> },
  { path: 'health/money', element: <SuspenseWrap><AdminMoneyPage /></SuspenseWrap> },
  { path: 'health/availability', element: <SuspenseWrap><AdminAvailabilityPage /></SuspenseWrap> },
  { path: 'health/queues', element: <SuspenseWrap><AdminQueuesPage /></SuspenseWrap> },
  { path: 'health/ai-quality', element: <SuspenseWrap><AdminAiQualityPage /></SuspenseWrap> },
  { path: 'health/vitals', element: <SuspenseWrap><AdminVitalsPage /></SuspenseWrap> },
  { path: 'health/latency', element: <SuspenseWrap><AdminLatencyPage /></SuspenseWrap> },
  { path: 'health/conveyor', element: <SuspenseWrap><AdminConveyorPage /></SuspenseWrap> },
  { path: 'manage/clients', element: <SuspenseWrap><AdminClientsPage /></SuspenseWrap> },
  { path: 'manage/projects', element: <SuspenseWrap><AdminProjectsPage /></SuspenseWrap> },
  { path: 'manage/audit', element: <SuspenseWrap><AdminAuditPage /></SuspenseWrap> },
  { path: 'manage/billing', element: <SuspenseWrap><AdminBillingPage /></SuspenseWrap> },
  { path: 'manage/migration', element: <SuspenseWrap><AdminMigrationPage /></SuspenseWrap> },
  { path: 'manage/notifications', element: <SuspenseWrap><AdminNotificationsPage /></SuspenseWrap> },
];

/**
 * Корень панели на `admin.<apex>`: адреса панели — /health/*, /manage/* без
 * приставки /admin; AdminRoute пропускает только администратора.
 */
export const ADMIN_ROOT_ELEMENT = (
  <AdminRoute>
    <SuspenseWrap><AdminLayoutPage /></SuspenseWrap>
  </AdminRoute>
);

// WEB-471: the panel now sits at the ROOT of admin.<apex> (see the "/" route
// in router/index.tsx). Legacy /admin/* links (old prefix) redirect to the new
// root so any stale bookmark still lands in the panel instead of 404ing.
export const ADMIN_LEGACY_ROUTES: RouteObject[] = [
  {
    path: '/admin/*',
    element: <Navigate to="/" replace />,
  },
];
